import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DetectRTC from "detectrtc";
import PropTypes from "prop-types";

import useLanguageChange from "../../../hooks/useLanguageChange";

import { PICKED_FILES_FROM, SELECTED_SCREEN, SUB_SCREEN, UPLOADING_FILE_STATUS, UPLOAD_FILES_DATA } from "../../../constants/UploadFilesConstants";

import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY, FIREBASE_LOG_VALUE } from "../../../constants/FirebaseConstants";
import {
    addUploadFileContainerImageInArray,
    changeUploadFileBothScreen,
    changeUploadFileScreen,
    changeUploadFileSubScreen,
    setUploadFileContainerImageArray,
    updateImagesArray,
} from "../../../reducers/uploadFile-reducer";
import { LogFirebase } from "../../../services/firestore";
import mediaDeviceService from "../../../services/mediaDeviceService";
import { CALL_DATA, PublicVariables } from "../../../constants/PublicVariables";
import uploadFilesService from "../../../services/uploadFilesService";
import { setNotAllowedCamera, setVideoError } from "../../../reducers/twilioEvents-reducer";
import { ERROR_TYPE } from "../../../constants/CallSettingConstants";

const CameraView = ({ updatedSelectedFiles }) => {
    const dispatch = useDispatch();
    const { currentCameraDevice } = useSelector((state) => state.callSetting);
    const { previousScreen, subScreenToShow, imagesArray, uploadFileContainerImages } = useSelector((state) => state.uploadFile);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    const [isCaptureButtonDisabled, setIsCaptureButtonDisabled] = useState(true);
    const [captureButtonText, setCaptureButtonText] = useState(localisedValues?.capture_image);

    const uploadFileCameraRef = useRef();
    const uploadFileVideoRef = useRef();

    useEffect(() => {
        uploadFileCameraRef.current?.classList?.add("active");

        showVideoView();
        return () => {
            uploadFileCameraRef.current?.classList?.remove("active");
        };
    }, []);

    const resetVariables = () => {
        setIsCaptureButtonDisabled(true);
        setCaptureButtonText(localisedValues?.capture_image);
        dispatch(setUploadFileContainerImageArray([]));
    };

    const showVideoView = async () => {
        if (!navigator.mediaDevices?.enumerateDevices) {
            console.error("enumerateDevices() not supported.");
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.NavigatorMediaDevices, FIREBASE_LOG_VALUE.EnumerateNotSupported);
            return;
        }

        const constraints = {
            video: mediaDeviceService.getVideoSource(currentCameraDevice),
        };
        navigator.mediaDevices
            .getUserMedia(constraints)
            .then((stream) => {
                uploadFileVideoRef.current.srcObject = stream;
                uploadFileVideoRef.current.controls = false;

                setIsCaptureButtonDisabled(false);

                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FromCamera, FIREBASE_LOG_VALUE.CameraStreamAvailable);
            })
            .catch((error) => {
                console.error(error);
                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.VideoCreationFailed, {
                    error_name: error.name,
                    error_msg: error.message,
                });
                let videoError = ERROR_TYPE.UNKNOWN;
                if (!DetectRTC.hasWebcam) {
                    videoError = ERROR_TYPE.NOT_FOUND;
                } else if (!DetectRTC.isWebsiteHasWebcamPermissions) {
                    videoError = ERROR_TYPE.PERMISSION;
                } else if (error.name === "NotReadableError" || error.message === "Could not start video source") {
                    videoError = ERROR_TYPE.CAM_IN_USE;
                } else if (DetectRTC.hasWebcam && DetectRTC.isWebsiteHasWebcamPermissions) {
                    videoError = ERROR_TYPE.NOT_FOUND;
                }
                dispatch(setNotAllowedCamera(true));
                dispatch(setVideoError(videoError));
            });
    };

    const getImageCountByStatus = (fileStatus = UPLOADING_FILE_STATUS.MARKUP, updatedImageArray = null) => {
        return (updatedImageArray ?? imagesArray).filter((image) => image.uploadingStatus === fileStatus).length;
    };

    const stopVideoSrc = () => {
        if (uploadFileVideoRef.current?.srcObject) {
            uploadFileVideoRef.current.srcObject.getTracks().map((t) => t.stop());
        }
    };

    const closeButtonClickHandler = () => {
        /*
            On Final upload view user clicking on add camera button 
            If user instanly perform close event then navigate to final view
        */
        if (previousScreen === SELECTED_SCREEN.FINAL_UPLODING_VIEW) {
            stopVideoSrc();
            resetVariables();

            dispatch(changeUploadFileBothScreen({ newScreen: SELECTED_SCREEN.FINAL_UPLODING_VIEW, newSubScreen: SUB_SCREEN.OFF_VIEW }));
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FromCamera, FIREBASE_LOG_VALUE.BackToFinalView);
            return;
        }

        if (subScreenToShow === SUB_SCREEN.CAMERA_VIEW || previousScreen === SELECTED_SCREEN.MARKUP_WITH_DONE) {
            stopVideoSrc();
            resetVariables();

            if (previousScreen === SELECTED_SCREEN.MARKUP_WITH_DONE) {
                dispatch(changeUploadFileSubScreen(SELECTED_SCREEN.FINAL_UPLODING_VIEW));
            }

            if (getImageCountByStatus() > 0) {
                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FromCamera, FIREBASE_LOG_VALUE.BackToMarkupWithoutSelection);
            } else {
                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.FromCamera, FIREBASE_LOG_VALUE.BackToSelectFileWithoutSelection);
                dispatch(changeUploadFileSubScreen(SELECTED_SCREEN.FINAL_UPLODING_VIEW));
                /* Update remaining count */
            }
        } else {
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.SendMessage, FIREBASE_LOG_VALUE.UploadFileEndNotification);
            dispatch(changeUploadFileScreen(SELECTED_SCREEN.OFF_VIEW));
            /* Handle closing upload files screen for upload files flow */
            /* Hide notify screen */
            /* Send data track */
            /* on exit upload file event for markup live stream */
            /* check for published video on exit upload file 
                case - 1 video is on before upload file
            */
            dispatch(updateImagesArray([]));
            /* check for local data track Reattach */
            /* Set Pointer view
                case -2 pointer is ON and user clicks on upload files
                this should be run after 504 sent, as mobile aaps will not have deligate registered on mark up screen
            */
            /* add z-index class to bottom bar if variable is set */
        }

        dispatch(changeUploadFileSubScreen(SUB_SCREEN.OFF_VIEW));
    };

    /* use this function to update text of capture button & enable/disable it
    send captureCamera parameter true for capture
    send captureCamera parameter false for done with capture */
    const toggleCaptureButtonText = (captureCamera) => {
        setIsCaptureButtonDisabled(captureCamera);
        setCaptureButtonText(captureCamera ? localisedValues?.capturing : localisedValues?.capture_image);
    };

    const handleCaptureButtonClick = async () => {
        LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.CapturePhotoClicked, FIREBASE_LOG_VALUE.Clicked);
        toggleCaptureButtonText(true);

        let width = null,
            height = null;

        const videoElem = uploadFileVideoRef.current;

        if (videoElem && videoElem.videoWidth != 0 && videoElem.videoHeight != 0) {
            width = videoElem.videoWidth;
            height = videoElem.videoHeight;
            if (PublicVariables.LD_CaptureDevice_GoogleAnalytics) {
                LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.VideoConstraints, { height: height, width: width });
            }
        } else {
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.VideoElementNotFound, "");
            return;
        }

        if (width && height) {
            uploadFileVideoRef.current?.classList?.add("take-photo-effect"); //Show capturing frame

            const canvas = document.getElementById("screenshotcanvas");
            const context = canvas.getContext("2d");
            canvas.width = width;
            canvas.height = height;
            context.drawImage(videoElem, 0, 0, width, height);

            const liveimgdata = canvas.toDataURL("image/jpeg");
            const latVal = CALL_DATA.Latitude;
            const longVal = CALL_DATA.Longitude;
            const timestamp = Math.floor(+new Date().getTime() / 10);
            const imageName = timestamp + "_markup.jpeg";
            const newContainerImage = {
                imageData: liveimgdata,
                imageSize: liveimgdata.length * (3 / 4),
                extension: "jpeg",
                lat: latVal,
                long: longVal,
                imageName: imageName,
            };

            dispatch(addUploadFileContainerImageInArray(newContainerImage));

            setTimeout(() => {
                uploadFileVideoRef.current?.classList?.remove("take-photo-effect");

                toggleCaptureButtonText(false);
            }, 500);
        }
    };

    const nextButtonClickHandler = () => {
        UPLOAD_FILES_DATA.pickedFilesFrom = PICKED_FILES_FROM.FROM_CAMERA;
        if (uploadFileContainerImages.length > 0) uploadFilesService.generateFilesArray(uploadFileContainerImages, updatedSelectedFiles, true);
        stopVideoSrc();
        resetVariables();
    };

    return (
        <div ref={uploadFileCameraRef} className='upload-file-camera' data-testid='uploadFileCamera'>
            <div className='upload-file-camera-in'>
                <div className='upload-file-camera-header'>
                    <div className='upload-file-camera-close custom-tooltip tooltip-auto-width'>
                        <button className='btn close-btn' onClick={() => closeButtonClickHandler()}>
                            <span className='btn-icon icon-close-light'></span>
                        </button>
                        <div className='custom-tooltip-content text-nowrap'>
                            <div className='custom-tooltip-in'>
                                <p>{localisedValues?.cancel}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='upload-file-camera-view' id='uploadCameraView'>
                    <video className='upload-file-video' id='uploadFilesVideo' ref={uploadFileVideoRef} playsInline autoPlay={true}></video>
                </div>
                <div className='upload-file-camera-menu'>
                    <div className='camera-action-menu'>
                        <div className='camera-action-button'>
                            <div className='camera-action-button-in'>
                                <button
                                    className='camera-action-btn'
                                    id='multipleCapture'
                                    disabled={isCaptureButtonDisabled}
                                    onClick={() => handleCaptureButtonClick()}
                                >
                                    <span className='camera-action-btn-icon'>
                                        <span className='for-icon icon-camera'></span>
                                    </span>
                                    <span className='for-text'>{captureButtonText}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='camera-right-menu'>
                        <div className='button'>
                            <button
                                className='btn btn-primary btn-with-icon right-icon camera-next-btn'
                                disabled={uploadFileContainerImages?.length === 0}
                                onClick={() => nextButtonClickHandler()}
                            >
                                <span className='btn-text'>{localisedValues?.next_lable}</span>
                                <span className='btn-icon icon-right-arrow'></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

CameraView.propTypes = {
    updatedSelectedFiles: PropTypes.func,
};

export default CameraView;
